/**
 * Returns a curried function of the provided function, so that:
 *
 * f(a, b, c) = f(a, b)(c) = f(a)(b)(c) = f(a)(b, c)
 *
 * @param {Function} f
 * @param {..*} Initial parameters
 * @return {Function} The curried function
 */
const curry = (f, ...args) => (args.length >= f.length ? f(...args) : curry.bind(this, f, ...args))

/**
 * Encapsulates switch/case or if/else logic.
 *
 * Takes a list of [predicate, transformer] pairs.
 *
 * The returned function passes its arguments to predicates, evaluates them, and execute the matched transformer (passing the
 * same arguments). If there's no matched predicate, return undefined.
 *
 * @param pairs Pairs of [predicate, transformer]
 * @return {Function} Encapsulated function
 */
const cond =
  (pairs) =>
    (...args) => {
      for (let i = 0; i < pairs.length; i++) {
        if (pairs[i][0].apply(this, args)) {
          return pairs[i][1].apply(this, args)
        }
      }

      return undefined
    }

/**
 * Functional wrapper for array map function.
 *
 * @param {Function} f
 * @param {*} arr
 */
const map = curry((f, arr) => (Array.isArray(arr) ? arr.map(f) : f(arr)))

/**
 * Partial application
 * @param {Function} f
 * @param {..*} args Initial parameters
 */
const partial = (f, ...args) => f.bind(this, ...args)

/**
 * Transform a value by chaining a list of function from left to right
 *
 * @param val
 * @param {..Function} funcs
 * @return {*}
 */
const pipe = (...funcs) =>
  function (val) {
    return funcs.reduce((acc, f) => f.apply(this, [acc]), val)
  }

/**
 * Get property of an object.
 *
 * This is a curried function.
 *
 * @param {string} prop
 * @param {Object} obj
 * @return {*}
 */
const getProp = curry((prop, obj) => {
  return obj[prop]
})

const setProp = curry((prop, value, obj) => {
  obj[prop] = value

  return obj
})

/**
 * Delay execution of callback used on individual items of a list.
 *
 * This function is curried.
 *
 * @param {number} initial Initial timeout
 * @param {number} step Delay per execution
 * @param {Function} callback Callback
 * @param {Array} items
 */

const whileDo = (pred, fn, initial) => (pred(initial) ? whileDo(pred, fn, fn(initial)) : initial)

/**
 * Get the nth element of an array
 *
 * @param {number}
 * @param {Array}
 * @return {*}
 */
const nth = curry((index, array) => (index < 0 ? array[array.length + index] : array[index]))

const last = nth(-1)
const add = curry((b, a) => a + b)

/**
 * Create an array out of an array-like object
 *
 * @param {Object} Array-like object
 * @return {Array} Array
 */
const makeArray = (arrayLike) => Array.prototype.slice.call(arrayLike)

const set = (item, selector) => {
  if (item instanceof Array) {
    for (const i of item) {
      i.classList.add(selector)
    }
  } else {
    item.classList.add(selector)
  }
}

const unset = (item, selector) => {
  if (item instanceof Array) {
    for (const i of item) {
      i.classList.remove(selector)
    }
  } else {
    item.classList.remove(selector)
  }
}

const hasOwnProperties = (property, obj) => {
  return Object.prototype.hasOwnProperty.call(obj, property)
}

const isEmpty = (val) => val == null || !(Object.keys(val) || val).length

const formatName = (string) =>
  string && typeof string === 'string' && string.includes('-') ? string.split('-')[1] : string

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const redirect = (handle, ctrlKey = false) => {
  const newUrl = `${window.location.origin + handle}`

  if (ctrlKey) {
    window.open(newUrl, '_blank').focus()

    return
  }
  window.location = newUrl
}

/**
 * Client has uploaded swatch images with naming convention
 * @param { string } styleCode
 * @param { string } colourCode
 * @param { number } [size]
 * @returns { string | null }
 */
const getBackgroundColor = (styleCode, colourCode, size = 16) => {
  if (!styleCode && !colourCode) {
    return null
  }

  return `url('https://${Shopify.cdnHost}/shop/files/${styleCode}_${colourCode}.jpg?width=${size}'), #CCCCCC`
}

/**
 * @param { string } input
 * @returns { unknown | null } some valid object like structure
 */
const tryJsonParse = (input) => {
  if (typeof input === 'undefined') {
    console.warn('Must provide input to tryJsonParse', input)

    return null
  }

  if (typeof input === 'object') return input

  try {
    return JSON.parse(input.replace(/&quot;/g, '"'))
  } catch (err) {
    console.error('Could not parse input', typeof input, input)

    return null
  }
}

const fetchProductByAjax = (handles) => {
  const allFetchs = handles.map((handle) => {
    return fetch(`${window.Shopify.routes.root}products/${handle}?view=ajax`)
      .then((response) => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json()
        } else {
          throw Error(response.statusText)
        }
      })
      .then((product) => {
        return product
      })
  })

  return Promise.allSettled(allFetchs)
    .then((results) =>
      results
        .filter(result => result.status === 'fulfilled')
        .map(results => results.value)
    )
}

/**
 * @param { string[] } tags
 * @param { boolean } [hasManyColours] defaults to false
 * @returns { string | string[] }
 */
const getColourTag = (tags, hasManyColours = false) => {
  if (!tags) return ''

  if (typeof tags === 'string') {
    tags = tags.split(',')
  }

  let colourCode = ''

  const colourCodeTags = tags
    .filter(tag => tag.includes('x-colourCode'))
    .map(tag => tag.split(':')[1])

  colourCode = hasManyColours
    ? colourCodeTags
    : colourCodeTags?.[0] || ''

  return colourCode
}

/**
 * @param { string | string[] } tags
 * @returns { string }
 */
const getStyleCodeByTag = (tags) => {
  if (!tags) return ''

  if (typeof tags === 'string') {
    tags = tags.split(',')
  }

  const styleCode = tags
    ?.find(tag => tag.includes('x-styleCode'))
    ?.map(tag => tag.split(':')[1])[0] ?? ''

  return styleCode
}

/**
 *
 * @param { { name: string, values: string[] }[] } optionsWithValues
 * @returns { string }
 */
const getColourName = (optionsWithValues) => {
  if (!optionsWithValues?.length) return ''

  const colourOption = optionsWithValues.find(option => ['color', 'colour'].includes(option.name.toLowerCase().trim()))

  if (colourOption?.values?.length) return colourOption.values[0]

  return ''
}

/**
 * A dash is incompatible with GraphQL aliases. Some stylecodes, and most handles include dashes.
 * After conversion, getActiveColoursByMarket returns the aliased version of the stylecode, and later
 * checks for a match must also use this method to find the correct data.
 * @param { string } inputString
 * @returns { string }
 */
const getAliasCompatibleString = (inputString) => {
  if (!inputString || typeof inputString !== 'string') {
    console.warn('getAliasCompatibleString must be provided a string inputString', typeof inputString, inputString)

    return ''
  }

  // replace dash w underscore
  let safeString = inputString.replace(/-/g, '_')

  // if safestring begins with a number
  if (safeString.match(/^[0-9]/g)?.length) {
    safeString = `a${safeString}`
  }

  return safeString
}

export {
  add,
  cond,
  makeArray,
  curry,
  getProp,
  last,
  map,
  nth,
  partial,
  pipe,
  setProp,
  whileDo,
  set,
  hasOwnProperties,
  isEmpty,
  formatName,
  delay,
  redirect,
  getBackgroundColor,
  tryJsonParse,
  fetchProductByAjax,
  getColourTag,
  getStyleCodeByTag,
  getColourName,
  getAliasCompatibleString
}
